import type {
	DamageCalculationInput,
	DamageCalculationResult,
	DokkanStats,
	FullCalculationResult,
} from "./types";
import { MAX_BASE_VALUE } from "./types";

const MAX_UINT32 = MAX_BASE_VALUE; // オーバーフローチェック用（2^32）

export function calculateATK(stats: DokkanStats): {
	value: number;
	overflow: boolean;
} {
	const {
		stats_atk,
		Lskill_atk_1,
		Lskill_atk_2,
		Pskill_atk_1,
		Pskill_atk_2,
		support_atk_1,
		support_atk_2,
		support_atk_active,
		support_atk_item,
		support_atk_field,
		support_atk_memory,
		link_atk,
		SA_atk,
		SA_power,
		SAboost_level,
		ki_bonus,
	} = stats;

	let atk = stats_atk;
	atk *= 1 + (Lskill_atk_1 + Lskill_atk_2) / 100;
	atk = Math.floor(atk);
	atk *= 1 + Pskill_atk_1 / 100 + support_atk_1 / 100;
	atk = Math.floor(atk);
	atk *= 1 + Pskill_atk_2 / 100 + support_atk_2 / 100;
	atk = Math.floor(atk);
	atk *= 1 + support_atk_active / 100;
	atk = Math.floor(atk);
	atk *= 1 + support_atk_item / 100;
	atk = Math.floor(atk);
	atk *= 1 + support_atk_field / 100;
	atk = Math.floor(atk);
	atk *= 1 + support_atk_memory / 100;
	atk = Math.floor(atk);
	atk *= 1 + link_atk / 100;
	atk = Math.floor(atk);
	atk *= ki_bonus;
	atk = Math.floor(atk);
	atk *= SA_power + SA_atk / 100 + SAboost_level * 0.05;

	const result = Math.round(atk);
	const overflow = result >= MAX_UINT32;
	return {
		value: overflow ? 0 : result,
		overflow,
	};
}

export function calculateActiveSkillATK(
	stats: DokkanStats,
	activeSkillPower: number,
	temporaryATKBoost: number,
	SABoostLevel: number,
): { value: number; overflow: boolean } {
	const {
		stats_atk,
		Lskill_atk_1,
		Lskill_atk_2,
		Pskill_atk_1,
		Pskill_atk_2,
		support_atk_1,
		support_atk_2,
		support_atk_active,
		support_atk_item,
		support_atk_field,
		support_atk_memory,
		link_atk,
		SA_atk,
		ki_bonus,
	} = stats;

	let atk = stats_atk;
	atk *= 1 + (Lskill_atk_1 + Lskill_atk_2) / 100;
	atk = Math.floor(atk);
	atk *= 1 + Pskill_atk_1 / 100 + support_atk_1 / 100;
	atk = Math.floor(atk);
	atk *= 1 + Pskill_atk_2 / 100 + support_atk_2 / 100;
	atk = Math.floor(atk);
	atk *= 1 + support_atk_active / 100;
	atk = Math.floor(atk);
	atk *= 1 + support_atk_item / 100;
	atk = Math.floor(atk);
	atk *= 1 + support_atk_field / 100;
	atk = Math.floor(atk);
	atk *= 1 + support_atk_memory / 100;
	atk = Math.floor(atk);
	atk *= 1 + link_atk / 100;
	atk = Math.floor(atk);

	const activeSkillMultiplier = activeSkillPower + temporaryATKBoost / 100;
	atk *= activeSkillMultiplier + SABoostLevel * 0.05;
	atk = Math.floor(atk);
	atk *= 1 + SA_atk / 100;
	atk = Math.floor(atk);
	atk *= ki_bonus;
	atk = Math.floor(atk);

	const result = Math.round(atk);
	const overflow = result >= MAX_UINT32;
	return {
		value: overflow ? 0 : result,
		overflow,
	};
}

export function calculateDEF(stats: DokkanStats): {
	value: number;
	overflow: boolean;
} {
	const {
		stats_def,
		Lskill_def_1,
		Lskill_def_2,
		Pskill_def_1,
		Pskill_def_2,
		support_def_1,
		support_def_2,
		support_def_active,
		support_def_item,
		support_def_field,
		support_def_memory,
		SA_def,
		link_def,
	} = stats;

	let def = stats_def;
	def *= 1 + (Lskill_def_1 + Lskill_def_2) / 100;
	def = Math.floor(def);
	def *= 1 + Pskill_def_1 / 100 + support_def_1 / 100;
	def = Math.floor(def);
	def *= 1 + Pskill_def_2 / 100 + support_def_2 / 100;
	def = Math.floor(def);
	def *= 1 + support_def_active / 100;
	def = Math.floor(def);
	def *= 1 + support_def_item / 100;
	def = Math.floor(def);
	def *= 1 + support_def_field / 100;
	def = Math.floor(def);
	def *= 1 + support_def_memory / 100;
	def = Math.floor(def);
	def *= 1 + SA_def / 100;
	def = Math.floor(def);
	def *= 1 + link_def / 100;
	def = Math.floor(def);

	const result = Math.round(def);
	const overflow = result >= MAX_UINT32;
	return {
		value: overflow ? 0 : result,
		overflow,
	};
}

export function calculateDamageTaken(
	input: DamageCalculationInput,
): DamageCalculationResult {
	const { enemy_atk, damage_reduction, player_def } = input;
	const baseDamage = Math.max(0, enemy_atk - player_def);
	const damageReductionMultiplier = 1 - damage_reduction / 100;
	const finalDamage = Math.round(baseDamage * damageReductionMultiplier);
	return {
		damage_taken: Math.max(0, finalDamage),
		damage_reduction_percentage: damage_reduction,
	};
}

export function calculateFullStats(
	stats: DokkanStats,
	damageInput?: DamageCalculationInput,
): FullCalculationResult {
	const atkResult = calculateATK(stats);
	const defResult = calculateDEF(stats);

	const result: FullCalculationResult = {
		atk: atkResult.value,
		def: defResult.value,
		atkOverflow: atkResult.overflow,
		defOverflow: defResult.overflow,
	};

	if (damageInput) {
		const damageCalcInput: DamageCalculationInput = {
			...damageInput,
			player_def: defResult.value,
		};
		result.damage_calculation = calculateDamageTaken(damageCalcInput);
	}

	return result;
}
